import { useState, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { elevationService, metersToSampleCount, DEFAULT_PROFILE_SAMPLING_LAYERS } from '../services/elevationService';
import { polygonSurfaceAreaService } from '../services/polygonSurfaceAreaService';
const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};
const calculatePolygonArea = (vertices) => {
    if (vertices.length < 3)
        return 0;
    const R = 6371000;
    let total = 0;
    for (let i = 0; i < vertices.length; i++) {
        const j = (i + 1) % vertices.length;
        const lat1 = vertices[i].lat * Math.PI / 180;
        const lat2 = vertices[j].lat * Math.PI / 180;
        const dLng = (vertices[j].lng - vertices[i].lng) * Math.PI / 180;
        total += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
    }
    return Math.abs(total * R * R / 2);
};
export const useServerArtifactView = (artifact, projectId) => {
    const [placemark, setPlacemark] = useState(null);
    const [measurement, setMeasurement] = useState(null);
    const [measurementCircle, setMeasurementCircle] = useState(null);
    const [path, setPath] = useState(null);
    const [polygon, setPolygon] = useState(null);
    const currentArtifactIdRef = useRef(null);
    useEffect(() => {
        setPlacemark(null);
        setMeasurement(null);
        setMeasurementCircle(null);
        setPath(null);
        setPolygon(null);
        if (!artifact) {
            currentArtifactIdRef.current = null;
            return;
        }
        currentArtifactIdRef.current = artifact.id;
        const artifactId = artifact.id;
        let geometry;
        try {
            geometry = JSON.parse(artifact.geometryJson);
        }
        catch (_a) {
            return;
        }
        if (artifact.artifactType === 'placemark') {
            const pm = {
                id: artifact.id,
                name: artifact.name,
                position: Cesium.Cartesian3.fromDegrees(geometry.lng, geometry.lat),
                color: artifact.color,
                icon: artifact.icon,
                showLabel: artifact.showLabel,
                scale: typeof geometry.scale === 'number' ? geometry.scale : undefined,
                elevation: undefined,
            };
            setPlacemark(pm);
            if (projectId) {
                elevationService.sampleElevation(projectId, geometry.lat, geometry.lng)
                    .then(elev => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setPlacemark(prev => prev ? { ...prev, elevation: elev } : null);
                    }
                })
                    .catch(() => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setPlacemark(prev => prev ? { ...prev, elevation: null } : null);
                    }
                });
            }
        }
        else if (artifact.artifactType === 'measurement') {
            const dist = calculateDistance(geometry.startLat, geometry.startLng, geometry.endLat, geometry.endLng);
            const VALID_DISTANCE_UNITS = ['m', 'km', 'ft', 'yd', 'mi'];
            const storedUnit = VALID_DISTANCE_UNITS.includes(artifact.displayUnit) ? artifact.displayUnit : 'ft';
            // Compute bearing the same way the live tool does, so view-mode
            // readouts match what the user saw when they created the line.
            const startCart = Cesium.Cartographic.fromDegrees(geometry.startLng, geometry.startLat);
            const endCart = Cesium.Cartographic.fromDegrees(geometry.endLng, geometry.endLat);
            const geodesic = new Cesium.EllipsoidGeodesic(startCart, endCart);
            const bearingDeg = ((Cesium.Math.toDegrees(geodesic.startHeading) % 360) + 360) % 360;
            const m = {
                kind: 'line',
                id: artifact.id,
                name: artifact.name,
                startPoint: Cesium.Cartesian3.fromDegrees(geometry.startLng, geometry.startLat),
                endPoint: Cesium.Cartesian3.fromDegrees(geometry.endLng, geometry.endLat),
                distanceMeters: dist,
                bearingDeg,
                color: artifact.color,
                displayUnit: storedUnit,
                elevationProfile: undefined,
                lineWidth: typeof geometry.lineWidth === 'number' ? geometry.lineWidth : 5,
                showLabel: typeof geometry.showLabel === 'boolean' ? geometry.showLabel : true,
            };
            setMeasurement(m);
            if (projectId) {
                elevationService.getLineElevationProfile(projectId, geometry.startLat, geometry.startLng, geometry.endLat, geometry.endLng, metersToSampleCount(dist), DEFAULT_PROFILE_SAMPLING_LAYERS).then(profile => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setMeasurement(prev => prev ? { ...prev, elevationProfile: profile || null } : null);
                    }
                }).catch(() => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setMeasurement(prev => prev ? { ...prev, elevationProfile: null } : null);
                    }
                });
            }
        }
        else if (artifact.artifactType === 'measurement_circle') {
            const VALID_DISTANCE_UNITS = ['m', 'km', 'ft', 'yd', 'mi'];
            const VALID_AREA_UNITS = ['sqm', 'sqft', 'acres', 'hectares'];
            const storedUnit = VALID_DISTANCE_UNITS.includes(artifact.displayUnit) ? artifact.displayUnit : 'ft';
            const storedAreaUnit = VALID_AREA_UNITS.includes(geometry.areaUnit) ? geometry.areaUnit : 'acres';
            const c = {
                kind: 'circle',
                id: artifact.id,
                name: artifact.name,
                center: Cesium.Cartesian3.fromDegrees(geometry.centerLng, geometry.centerLat),
                radiusMeters: typeof geometry.radiusMeters === 'number' ? geometry.radiusMeters : 0,
                // Entities are owned by the renderer for server artifacts; view-only
                // consumers don't drag them, so we expose `null` Entity placeholders.
                centerEntity: undefined,
                circleEntity: undefined,
                labelEntity: undefined,
                color: artifact.color,
                outlineColor: typeof geometry.outlineColor === 'string' ? geometry.outlineColor : undefined,
                fillOpacity: typeof geometry.fillOpacity === 'number' ? geometry.fillOpacity : undefined,
                showLabel: typeof geometry.showLabel === 'boolean' ? geometry.showLabel : false,
                displayUnit: storedUnit,
                areaUnit: storedAreaUnit,
                lineWidth: typeof geometry.lineWidth === 'number' ? geometry.lineWidth : 3,
            };
            setMeasurementCircle(c);
        }
        else if (artifact.artifactType === 'path') {
            const points = (geometry.points || []);
            const pathPoints = points.map((p, i) => ({
                id: `${artifact.id}-pt-${i}`,
                position: Cesium.Cartesian3.fromDegrees(p.lng, p.lat),
            }));
            const segments = [];
            let totalDistance = 0;
            for (let i = 0; i < points.length - 1; i++) {
                const dist = calculateDistance(points[i].lat, points[i].lng, points[i + 1].lat, points[i + 1].lng);
                totalDistance += dist;
                segments.push({
                    id: `${artifact.id}-seg-${i}`,
                    startPointId: pathPoints[i].id,
                    endPointId: pathPoints[i + 1].id,
                    distanceMeters: dist,
                    elevationProfile: undefined,
                });
            }
            const VALID_DISTANCE_UNITS = ['m', 'km', 'ft', 'yd', 'mi'];
            const storedUnit = VALID_DISTANCE_UNITS.includes(artifact.displayUnit) ? artifact.displayUnit : 'ft';
            const buffer = geometry.buffer && Array.isArray(geometry.buffer.polygons) && geometry.buffer.polygons.length > 0
                ? geometry.buffer
                : null;
            const p = {
                id: artifact.id,
                name: artifact.name,
                points: pathPoints,
                segments,
                totalDistanceMeters: totalDistance,
                color: artifact.color,
                displayUnit: storedUnit,
                totalElevationProfile: undefined,
                lineWidth: typeof geometry.lineWidth === 'number' ? geometry.lineWidth : 5,
                ...(buffer ? {
                    bufferDistanceMeters: typeof buffer.distance_meters === 'number' ? buffer.distance_meters : undefined,
                    bufferUnit: buffer.unit === 'm' ? 'm' : 'ft',
                    bufferAreaSqMeters: typeof buffer.area_sq_meters === 'number' ? buffer.area_sq_meters : undefined,
                    bufferPolygons: buffer.polygons,
                } : {}),
            };
            setPath(p);
            if (projectId && points.length >= 2) {
                elevationService.getPolylineElevationProfile(projectId, points.map(p => ({ lat: p.lat, lng: p.lng })), metersToSampleCount(totalDistance), DEFAULT_PROFILE_SAMPLING_LAYERS)
                    .then(profile => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setPath(prev => prev ? { ...prev, totalElevationProfile: profile || null } : null);
                    }
                })
                    .catch(() => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setPath(prev => prev ? { ...prev, totalElevationProfile: null } : null);
                    }
                });
                if (points.length > 2) {
                    for (let i = 0; i < points.length - 1; i++) {
                        const segIdx = i;
                        const segDist = segments[i].distanceMeters;
                        elevationService.getLineElevationProfile(projectId, points[i].lat, points[i].lng, points[i + 1].lat, points[i + 1].lng, metersToSampleCount(segDist), DEFAULT_PROFILE_SAMPLING_LAYERS).then(profile => {
                            if (currentArtifactIdRef.current === artifactId) {
                                setPath(prev => {
                                    if (!prev)
                                        return null;
                                    const updatedSegments = [...prev.segments];
                                    if (updatedSegments[segIdx]) {
                                        updatedSegments[segIdx] = { ...updatedSegments[segIdx], elevationProfile: profile || null };
                                    }
                                    return { ...prev, segments: updatedSegments };
                                });
                            }
                        }).catch(() => {
                            if (currentArtifactIdRef.current === artifactId) {
                                setPath(prev => {
                                    if (!prev)
                                        return null;
                                    const updatedSegments = [...prev.segments];
                                    if (updatedSegments[segIdx]) {
                                        updatedSegments[segIdx] = { ...updatedSegments[segIdx], elevationProfile: null };
                                    }
                                    return { ...prev, segments: updatedSegments };
                                });
                            }
                        });
                    }
                }
            }
        }
        else if (artifact.artifactType === 'polygon') {
            const verts = (geometry.vertices || []);
            const polygonVertices = verts.map((v, i) => ({
                id: `${artifact.id}-v-${i}`,
                position: Cesium.Cartesian3.fromDegrees(v.lng, v.lat),
            }));
            const edges = [];
            let perimeter = 0;
            for (let i = 0; i < verts.length; i++) {
                const j = (i + 1) % verts.length;
                const dist = calculateDistance(verts[i].lat, verts[i].lng, verts[j].lat, verts[j].lng);
                perimeter += dist;
                edges.push({
                    id: `${artifact.id}-edge-${i}`,
                    startVertexIndex: i,
                    endVertexIndex: j,
                    distanceMeters: dist,
                    elevationProfile: undefined,
                });
            }
            const area = calculatePolygonArea(verts);
            const storedUnit = (['sqm', 'sqft', 'acres', 'hectares'].includes(artifact.displayUnit || '') ? artifact.displayUnit : 'sqft');
            const pg = {
                id: artifact.id,
                name: artifact.name,
                vertices: polygonVertices,
                edges,
                areaSquareMeters: area,
                perimeterMeters: perimeter,
                color: artifact.color,
                displayUnit: storedUnit,
                lineWidth: typeof geometry.lineWidth === 'number' ? geometry.lineWidth : 5,
                // Surface area isn't persisted with the artifact; recompute it on view
                // so saved polygons show the same LiDAR-aware figure as the live tool.
                surfaceAreaLoading: projectId !== null && verts.length >= 3,
            };
            setPolygon(pg);
            // Fetch the LiDAR surface area (parity with usePolygons.fetchSurfaceArea).
            if (projectId && verts.length >= 3) {
                const ring = verts.map(v => [v.lng, v.lat]);
                ring.push([verts[0].lng, verts[0].lat]);
                const geojson = JSON.stringify({ type: 'Polygon', coordinates: [ring] });
                polygonSurfaceAreaService.calculate({
                    projectId,
                    polygonGeojson: geojson,
                    planarAreaM2: area,
                }).then(result => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setPolygon(prev => prev ? {
                            ...prev,
                            surfaceAreaLoading: false,
                            surfaceAreaSquareMeters: result.surfaceAreaM2,
                            surfaceAreaCoveredFraction: result.coveredFraction,
                        } : null);
                    }
                }).catch(() => {
                    if (currentArtifactIdRef.current === artifactId) {
                        setPolygon(prev => prev ? { ...prev, surfaceAreaLoading: false } : null);
                    }
                });
            }
            if (projectId && verts.length >= 3) {
                for (let i = 0; i < verts.length; i++) {
                    const j = (i + 1) % verts.length;
                    const edgeIdx = i;
                    const edgeDist = edges[i].distanceMeters;
                    elevationService.getLineElevationProfile(projectId, verts[i].lat, verts[i].lng, verts[j].lat, verts[j].lng, metersToSampleCount(edgeDist)).then(profile => {
                        if (currentArtifactIdRef.current === artifactId) {
                            setPolygon(prev => {
                                if (!prev)
                                    return null;
                                const updatedEdges = [...prev.edges];
                                if (updatedEdges[edgeIdx]) {
                                    updatedEdges[edgeIdx] = { ...updatedEdges[edgeIdx], elevationProfile: profile || null };
                                }
                                return { ...prev, edges: updatedEdges };
                            });
                        }
                    }).catch(() => {
                        if (currentArtifactIdRef.current === artifactId) {
                            setPolygon(prev => {
                                if (!prev)
                                    return null;
                                const updatedEdges = [...prev.edges];
                                if (updatedEdges[edgeIdx]) {
                                    updatedEdges[edgeIdx] = { ...updatedEdges[edgeIdx], elevationProfile: null };
                                }
                                return { ...prev, edges: updatedEdges };
                            });
                        }
                    });
                }
            }
        }
    }, [artifact === null || artifact === void 0 ? void 0 : artifact.id, artifact === null || artifact === void 0 ? void 0 : artifact.artifactType, projectId]);
    return { placemark, measurement, measurementCircle, path, polygon };
};
